// src/lib/utils/format/numbers.ts
// AUTO-SPLIT from lib/utils/formatters.ts (A10/A2). New code: import from ~/lib/utils/format.
import type { CurrencyOptions } from './types'

// =============================================================================
// CACHED FORMATTERS
// =============================================================================

/**
 * Pre-cached NumberFormat instances for optimal performance.
 * These are created once and reused throughout the application lifecycle.
 */
const numberFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
})

const compactFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  minimumFractionDigits: 0,
  maximumFractionDigits: 1
})

/**
 * Cache for dynamically created formatters to improve performance.
 * Uses WeakMap for automatic memory management when formatters are no longer needed.
 */
const formatterCache = new Map<string, Intl.NumberFormat>()

// =============================================================================
// HELPER FUNCTIONS (INTERNAL)
// =============================================================================

/**
 * Validates that a value is a finite number and throws a descriptive error if not.
 * 
 * @param {unknown} value - The value to validate
 * @param {string} [context='Value'] - Context for the error message
 * @throws {TypeError} If value is not a finite number
 * @internal
 */
function validateFiniteNumber(value: unknown, context = 'Value'): asserts value is number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new TypeError(`${context} must be a finite number, received: ${typeof value}`)
  }
}

/**
 * Ensures a decimal value is within the valid range for Intl.NumberFormat (0-20).
 * 
 * @param {number} decimals - The decimal value to clamp
 * @returns {number} Clamped decimal value between 0 and 20
 * @internal
 */
function clampDecimals(decimals: number): number {
  return Math.max(0, Math.min(20, Math.floor(decimals)))
}

/**
 * Creates or retrieves a cached NumberFormat instance for optimal performance.
 * Implements fallback to en-US locale if the requested locale is not supported.
 * 
 * @param {string} locale - The locale to use
 * @param {Intl.NumberFormatOptions} options - Formatting options
 * @returns {Intl.NumberFormat} Cached or new formatter
 * @internal
 */
function getOrCreateFormatter(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}:${JSON.stringify(options)}`
  
  if (!formatterCache.has(key)) {
    try {
      formatterCache.set(key, new Intl.NumberFormat(locale, options))
    } catch (error) {
      // Fallback to en-US if locale is not supported
      if (process.env.NODE_ENV === 'development') {
        console.warn(`utils/formatters: Unsupported locale ${locale}, falling back to en-US`, error)
      }
      const fallbackKey = `en-US:${JSON.stringify(options)}`
      if (!formatterCache.has(fallbackKey)) {
        formatterCache.set(fallbackKey, new Intl.NumberFormat('en-US', options))
      }
      const fallbackFormatter = formatterCache.get(fallbackKey)
      if (!fallbackFormatter) {
        throw new Error('Failed to create fallback formatter')
      }
      return fallbackFormatter
    }
  }
  
  const formatter = formatterCache.get(key)
  if (!formatter) {
    throw new Error('Failed to retrieve formatter from cache')
  }
  return formatter
}


/**
 * Format a number with thousands separators and optional decimal places.
 * Provides comprehensive number formatting with graceful error handling.
 *
 * @param {number} value - The number to format
 * @param {number} [decimals] - Optional number of decimal places (0-20)
 * @param {string} [locale='en-US'] - Locale for formatting
 * @returns {string} Formatted string with thousands separators (e.g. "1,234,567")
 * @throws {TypeError} If value is not a finite number
 * 
 * @example
 * ```typescript
 * formatNumber(1234567) // Returns "1,234,567"
 * formatNumber(1234.567, 2) // Returns "1,234.57"
 * formatNumber(0) // Returns "0"
 * formatNumber(1234567, 0, 'de-DE') // Returns "1.234.567" (German formatting)
 * ```
 */
export function formatNumber(value: number, decimals?: number, locale = 'en-US'): string {
  validateFiniteNumber(value, 'Value')
  
  if (decimals !== undefined) {
    const safeDecimals = clampDecimals(decimals)
    
    try {
      const formatter = getOrCreateFormatter(locale, {
        minimumFractionDigits: safeDecimals,
        maximumFractionDigits: safeDecimals
      })
      return formatter.format(value)
    } catch (error) {
      // Fallback in case of any Intl.NumberFormat errors
      if (process.env.NODE_ENV === 'development') {
        console.warn('utils/formatters: Error formatting number with decimals', error)
      }
      return value.toFixed(safeDecimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
  }
  
  if (locale !== 'en-US') {
    try {
      const formatter = getOrCreateFormatter(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })
      return formatter.format(value)
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('utils/formatters: Error formatting number with locale', error)
      }
    }
  }
  
  return numberFormatter.format(value)
}

/**
 * Format a number as USD currency with backward compatibility for legacy API.
 *
 * @param {number} value - The number to format as currency
 * @param {number} [decimals] - Number of decimal places (0-20)
 * @param {boolean} [showSymbol=true] - Whether to show the currency symbol
 * @returns {string} Formatted string with $ symbol and thousands separators (e.g. "$1,234")
 * @throws {TypeError} If value is not a finite number
 * 
 * @example
 * ```typescript
 * formatCurrency(1234) // Returns "$1,234"
 * formatCurrency(1234.56, 2) // Returns "$1,234.56"
 * formatCurrency(1234, 0, false) // Returns "1,234"
 * formatCurrency(-500) // Returns "-$500"
 * ```
 */
export function formatCurrency(value: number, decimals?: number, showSymbol?: boolean): string
/**
 * Format a number as currency with comprehensive options.
 *
 * @param {number} value - The number to format as currency
 * @param {CurrencyOptions} options - Options object for currency formatting
 * @returns {string} Formatted currency string
 * @throws {TypeError} If value is not a finite number
 * 
 * @example
 * ```typescript
 * formatCurrency(1234, { decimals: 2, currency: 'EUR' }) // Returns "€1,234.56"
 * formatCurrency(1234, { decimals: 0, showSymbol: false }) // Returns "1,234"
 * ```
 */
export function formatCurrency(value: number, options: CurrencyOptions): string
export function formatCurrency(
  value: number,
  decimalsOrOptions?: number | CurrencyOptions,
  showSymbol = true
): string {
  validateFiniteNumber(value, 'Value')
  
  // Handle backward compatibility - check if it's legacy API (number or undefined as second param)
  if (typeof decimalsOrOptions === 'number' || decimalsOrOptions === undefined) {
    const decimals = decimalsOrOptions
    const safeDecimals = decimals === undefined ? 0 : clampDecimals(decimals)
    
    try {
      const formatter = getOrCreateFormatter('en-US', {
        style: showSymbol ? 'currency' : 'decimal',
        currency: showSymbol ? 'USD' : undefined,
        minimumFractionDigits: safeDecimals,
        maximumFractionDigits: safeDecimals
      })
      return formatter.format(value)
    } catch (error) {
      // Fallback in case of any Intl.NumberFormat errors
      if (process.env.NODE_ENV === 'development') {
        console.warn('utils/formatters: Error formatting currency', error)
      }
      const formattedNumber = value.toFixed(safeDecimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      return showSymbol ? `$${formattedNumber}` : formattedNumber
    }
  }
  
  // Handle modern API with options object
  const options = decimalsOrOptions
  const {
    decimals,
    showSymbol: optionsShowSymbol = true,
    currency = 'USD',
    locale = 'en-US'
  } = options
  
  const safeDecimals = decimals === undefined ? 0 : clampDecimals(decimals)
  
  try {
    const formatter = getOrCreateFormatter(locale, {
      style: optionsShowSymbol ? 'currency' : 'decimal',
      currency: optionsShowSymbol ? currency : undefined,
      minimumFractionDigits: safeDecimals,
      maximumFractionDigits: safeDecimals
    })
    return formatter.format(value)
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('utils/formatters: Error formatting currency with options', error)
    }
    const formattedNumber = value.toFixed(safeDecimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    
    if (optionsShowSymbol) {
      // Simple currency symbol mapping for fallback
      const currencySymbols: Record<string, string> = {
        'USD': '$',
        'EUR': '€',
        'GBP': '£',
        'JPY': '¥'
      }
      const symbol = currencySymbols[currency] ?? currency
      return `${symbol}${formattedNumber}`
    }
    
    return formattedNumber
  }
}

/**
 * Format a number as a percentage with precise control over decimal places.
 * Provides consistent percentage formatting with graceful error handling.
 *
 * @param {number} value - The decimal value to format (e.g. 0.1234 for 12.34%)
 * @param {number} [decimals=1] - Number of decimal places to show (0-20)
 * @param {boolean} [showSymbol=true] - Whether to show the % symbol
 * @returns {string} Formatted percentage string with % symbol
 * @throws {TypeError} If value is not a finite number or is negative
 * 
 * @example
 * ```typescript
 * formatPercent(0.1234) // Returns "12.3%"
 * formatPercent(0.1234, 2) // Returns "12.34%"
 * formatPercent(0.1234, 0, false) // Returns "12"
 * formatPercent(1.5) // Returns "150.0%"
 * ```
 */
export function formatPercent(value: number, decimals = 1, showSymbol = true): string {
  validateFiniteNumber(value, 'Value')
  
  const safeDecimals = clampDecimals(decimals)
  const percentValue = value * 100
  
  if (showSymbol) {
    return `${percentValue.toFixed(safeDecimals)}%`
  }
  return percentValue.toFixed(safeDecimals)
}

/**
 * Format a number in compact notation (K, M, B, T) with optimal performance.
 * Uses cached formatters for consistent performance across the application.
 *
 * @param {number} value - The number to format
 * @param {number} [decimals] - Optional number of decimal places (0-20)
 * @returns {string} Formatted string with compact notation (e.g. "1.2M")
 * @throws {TypeError} If value is not a finite number
 * 
 * @example
 * ```typescript
 * formatCompact(1200000) // Returns "1.2M"
 * formatCompact(1230000, 2) // Returns "1.23M"
 * formatCompact(500) // Returns "500"
 * formatCompact(1500000000) // Returns "1.5B"
 * ```
 */
export function formatCompact(value: number, decimals?: number): string {
  validateFiniteNumber(value, 'Value')
  
  if (decimals !== undefined) {
    const safeDecimals = clampDecimals(decimals)
    
    try {
      const formatter = new Intl.NumberFormat('en-US', {
        notation: 'compact',
        minimumFractionDigits: safeDecimals,
        maximumFractionDigits: safeDecimals
      })
      return formatter.format(value)
    } catch (error) {
      // Fallback for compact formatting
      if (process.env.NODE_ENV === 'development') {
        console.warn('utils/formatters: Error formatting compact number', error)
      }
      const suffixes = ['', 'K', 'M', 'B', 'T']
      let suffixIndex = 0
      let scaledValue = value
      
      while (scaledValue >= 1000 && suffixIndex < suffixes.length - 1) {
        scaledValue /= 1000
        suffixIndex++
      }
      
      return `${scaledValue.toFixed(safeDecimals)}${suffixes[suffixIndex]}`
    }
  }
  
  return compactFormatter.format(value)
}

/**
 * Format a duration in milliseconds to appropriate units.
 *
 * @param {number} value - Duration in milliseconds
 * @param {boolean} [verbose=false] - Whether to use verbose format (e.g. "5 minutes" vs "5m")
 * @returns {string} Formatted duration with units (d, h, m, s, ms)
 * @throws {TypeError} If value is not a finite number or is negative
 * 
 * @example
 * ```typescript
 * formatDuration(300000) // Returns "5m" (5 minutes)
 * formatDuration(300000, true) // Returns "5 minutes"
 * formatDuration(5400000) // Returns "1.5h" (1.5 hours)
 * formatDuration(1000) // Returns "1s"
 * formatDuration(500) // Returns "500ms"
 * ```
 */
export function formatDuration(value: number, verbose = false): string {
  validateFiniteNumber(value, 'Duration')
  
  if (value < 0) {
    throw new TypeError('Duration must be non-negative')
  }
  
  const units: Array<[number, string, string]> = [
    [1000 * 60 * 60 * 24, 'd', 'day'],
    [1000 * 60 * 60, 'h', 'hour'],
    [1000 * 60, 'm', 'minute'],
    [1000, 's', 'second'],
    [1, 'ms', 'millisecond']
  ]
  
  for (const [threshold, shortSuffix, longSuffix] of units) {
    if (value >= threshold) {
      const unitValue = value / threshold
      const roundedValue = Math.round(unitValue * 10) / 10
      
      if (verbose) {
        const pluralSuffix = roundedValue === 1 ? '' : 's'
        return `${roundedValue} ${longSuffix}${pluralSuffix}`
      }
      
      return `${roundedValue}${shortSuffix}`
    }
  }
  
  return `${value}${verbose ? ' milliseconds' : 'ms'}`
}

/**
 * Format a number as a ratio (e.g., 0.5 as "1:2").
 * Automatically simplifies ratios to their lowest common terms for clarity.
 *
 * @param {number} value - The ratio value (e.g., 0.5)
 * @param {boolean} [simplify=true] - Whether to simplify the ratio to lowest terms
 * @returns {string} Formatted ratio string (e.g., "1:2")
 * @throws {TypeError} If value is not a finite number or is negative
 * 
 * @example
 * ```typescript
 * formatRatio(0.5) // Returns "1:2"
 * formatRatio(0.667) // Returns "2:3"
 * formatRatio(2) // Returns "2:1"
 * formatRatio(0) // Returns "0:1"
 * ```
 */
export function formatRatio(value: number, simplify = true): string {
  validateFiniteNumber(value, 'Ratio value')
  
  if (value < 0) {
    throw new TypeError('Ratio value must be non-negative')
  }
  
  if (value === 0) return "0:1"
  if (!isFinite(value)) return "∞:1"
  
  // For very small values, return a simplified form
  if (value < 0.01) return "1:100+"
  
  // Calculate GCD for simplifying fractions
  const gcd = (a: number, b: number): number => {
    return b === 0 ? a : gcd(b, a % b)
  }
  
  // Convert to fraction with reasonable precision
  const precision = 1000
  let numerator = Math.round(value * precision)
  let denominator = precision
  
  // Simplify the fraction if requested
  if (simplify && numerator !== 0) {
    const divisor = gcd(numerator, denominator)
    numerator = numerator / divisor
    denominator = denominator / divisor
  }
  
  return `${numerator}:${denominator}`
}

/**
 * Format a number for display in calculators and dashboards with null safety.
 * Provides consistent formatting across different contexts with graceful null handling.
 * 
 * @param {number | null | undefined} value - The number to format
 * @param {'number' | 'currency' | 'percent' | 'compact'} [format='number'] - Format type
 * @param {number} [decimals] - Number of decimal places (0-20)
 * @returns {string} Formatted number string
 * @throws {TypeError} If decimals is provided but not a valid number
 * 
 * @example
 * ```typescript
 * formatNumberForDisplay(1234.56, 'currency', 2) // Returns "$1,234.56"
 * formatNumberForDisplay(0.1234, 'percent', 1) // Returns "12.3%"
 * formatNumberForDisplay(1500000, 'compact') // Returns "1.5M"
 * formatNumberForDisplay(null) // Returns "—"
 * ```
 */
export function formatNumberForDisplay(
  value: number | null | undefined, 
  format: 'number' | 'currency' | 'percent' | 'compact' = 'number', 
  decimals?: number
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—'
  }
  
  // Validate decimals parameter if provided
  if (decimals !== undefined && (typeof decimals !== 'number' || !Number.isFinite(decimals))) {
    throw new TypeError('Decimals must be a finite number')
  }
  
  const safeDecimals = decimals !== undefined ? clampDecimals(decimals) : undefined
  
  switch (format) {
    case 'currency':
      return formatCurrency(value, safeDecimals)
    case 'percent':
      return formatPercent(value, safeDecimals ?? 1)
    case 'compact':
      return formatCompact(value, safeDecimals)
    case 'number':
    default:
      return formatNumber(value, safeDecimals)
  }
}

/**
 * Format a number as an ordinal (1st, 2nd, 3rd, etc.)
 * 
 * @param {number} n - The number to format
 * @returns {string} Formatted ordinal string
 * @throws {TypeError} If n is not an integer
 * 
 * @example
 * ```typescript
 * formatOrdinal(1) // Returns "1st"
 * formatOrdinal(22) // Returns "22nd"
 * formatOrdinal(103) // Returns "103rd"
 * formatOrdinal(11) // Returns "11th"
 * ```
 */
export function formatOrdinal(n: number): string {
  if (!Number.isInteger(n)) {
    throw new TypeError('Value must be an integer')
  }
  
  const v = Math.abs(n) % 100
  
  // Handle special cases for 11th, 12th, 13th
  if (v >= 11 && v <= 13) {
    return n + 'th'
  }
  
  // Get the last digit to determine the ordinal suffix
  const lastDigit = v % 10
  
  switch (lastDigit) {
    case 1: return n + 'st'
    case 2: return n + 'nd'
    case 3: return n + 'rd'
    default: return n + 'th'
  }
}

// =============================================================================
// SAFE STRING CONVERSION FUNCTIONS
// =============================================================================

